// Lead Nurture Command Centre — shared helpers (pure, no side effects).
import {
  Flame, Thermometer, Droplet, Snowflake, PhoneCall, MessageSquare, Mail,
  ExternalLink, Target, UserCheck, Clock, Reply, Rocket, AlertCircle, Moon,
} from 'lucide-react';

export const API = process.env.REACT_APP_BACKEND_URL;

// ─── Formatting ───────────────────────────────────────────────
export const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; } };
export const fmtDateTime = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };
export const fmtRel = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (Math.abs(mins) < 1) return 'now';
    if (mins >= 0 && mins < 60) return `${mins}m ago`;
    if (mins < 0 && mins > -60) return `in ${-mins}m`;
    const hrs = Math.round(mins / 60);
    if (hrs >= 0 && hrs < 24) return `${hrs}h ago`;
    if (hrs < 0 && hrs > -24) return `in ${-hrs}h`;
    const days = Math.round(hrs / 24);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days === -1) return 'tomorrow';
    if (days > 0) return `${days}d ago`;
    return `in ${-days}d`;
  } catch { return iso; }
};
export const fmtMoney = (n) => {
  if (n === null || n === undefined || n === '' || isNaN(Number(n))) return null;
  const v = Number(n);
  if (v >= 1000000) return `$${(v / 1000000).toFixed(2)}M`;
  if (v >= 1000) return `$${Math.round(v / 1000)}k`;
  return `$${v}`;
};
export const daysSince = (iso) => { if (!iso) return null; try { return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); } catch { return null; } };
export const daysUntil = (iso) => { if (!iso) return null; try { return Math.floor((new Date(iso).getTime() - Date.now()) / 86400000); } catch { return null; } };
const minsSince = (iso) => { if (!iso) return null; try { return Math.floor((Date.now() - new Date(iso).getTime()) / 60000); } catch { return null; } };

// ─── Temperature (4 levels) ───────────────────────────────────
export const TEMP_META = {
  hot:     { key: 'hot',     label: 'Hot',     icon: Flame,       badge: 'bg-rose-500',  chip: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500' },
  warm:    { key: 'warm',    label: 'Warm',    icon: Thermometer, badge: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 ring-amber-200', dot: 'bg-amber-500' },
  nurture: { key: 'nurture', label: 'Nurture', icon: Droplet,     badge: 'bg-blue-500',  chip: 'bg-blue-50 text-blue-700 ring-blue-200', dot: 'bg-blue-500' },
  cold:    { key: 'cold',    label: 'Cold',    icon: Snowflake,   badge: 'bg-slate-400', chip: 'bg-slate-100 text-slate-600 ring-slate-200', dot: 'bg-slate-400' },
};
export const TEMP_ORDER = ['hot', 'warm', 'nurture', 'cold'];

// Resolve a lead's effective temperature. Manual `temperature` wins; else derive
// from the auto lead_temperature / priority; long-term-nurture status → nurture.
export const effectiveTemp = (l) => {
  const manual = (l?.temperature || '').toLowerCase();
  if (TEMP_META[manual]) return manual;
  if (l?.status === 'long_term_nurture' || (l?.nurture_until && new Date(l.nurture_until) > new Date())) return 'nurture';
  const auto = (l?.lead_temperature || l?.priority || '').toLowerCase();
  if (auto === 'hot') return 'hot';
  if (auto === 'warm') return 'warm';
  return 'cold';
};

// ─── Pipeline stages ──────────────────────────────────────────
export const PIPELINE_STAGES = [
  { key: 'new_lead',           label: 'New Lead',           color: 'blue' },
  { key: 'contact_attempted',  label: 'Contact Attempted',  color: 'sky' },
  { key: 'engaged',            label: 'Engaged',            color: 'cyan' },
  { key: 'discovery_booked',   label: 'Discovery Booked',   color: 'violet' },
  { key: 'discovery_completed',label: 'Discovery Completed', color: 'indigo' },
  { key: 'portal_sent',        label: 'Portal Sent',        color: 'orange' },
  { key: 'portal_started',     label: 'Portal Started',     color: 'amber' },
  { key: 'long_term_nurture',  label: 'Long-Term Nurture',  color: 'blue' },
  { key: 'ready_to_proceed',   label: 'Ready to Proceed',   color: 'emerald' },
  { key: 'converted',          label: 'Converted',          color: 'emerald' },
  { key: 'lost',               label: 'Lost',               color: 'slate' },
];
export const STAGE_META = Object.fromEntries(PIPELINE_STAGES.map((s) => [s.key, s]));

const portalStarted = (l) => !!(l?.portal_opened_at || l?.portal_started_at || l?.pod_stage === 'portal_in_progress');
const repliedRecently = (l) => {
  const m = minsSince(l?.last_client_response_at || l?.last_response_at);
  return m !== null && m <= 2880; // 2 days
};

export const computePipelineStage = (l) => {
  if (!l) return 'new_lead';
  if (l.pipeline_stage && STAGE_META[l.pipeline_stage]) return l.pipeline_stage;
  const s = l.status;
  if (s === 'lost') return 'lost';
  if (s === 'converted' || s === 'graduated' || s === 'moved_to_client_portal') return 'converted';
  if (s === 'long_term_nurture' || (l.nurture_until && new Date(l.nurture_until) > new Date())) return 'long_term_nurture';
  if (s === 'ready_to_proceed' || l.portal_completed_at) return 'ready_to_proceed';
  if (l.portal_sent_at && portalStarted(l)) return 'portal_started';
  if (l.portal_sent_at) return 'portal_sent';
  if (l.discovery_call_at) return 'discovery_completed';
  if ((l.next_action || '').toLowerCase().includes('discovery') || s === 'discovery') return 'discovery_booked';
  if (repliedRecently(l) || s === 'contacted' || s === 'educating' || s === 'pre_qualifying') return 'engaged';
  if (l.first_contacted_at || (l.contact_count || 0) > 0 || (l.contact_attempts || 0) > 0) return 'contact_attempted';
  return 'new_lead';
};

// ─── Next Best Action (deterministic DerwentAI rules) ─────────
export const computeNBA = (l) => {
  if (!l) return { label: 'Review lead', icon: Target, tone: 'slate' };
  if (l.nurture_phase === 'historical' || isPreReset(l)) return { label: 'Historical lead — reactivate to resume follow-ups', icon: Moon, tone: 'slate' };
  const s = l.status;
  if (s === 'lost') return { label: 'Closed — reopen if revived', icon: AlertCircle, tone: 'slate' };
  if (s === 'converted' || s === 'moved_to_client_portal' || s === 'graduated') return { label: 'Moved to Client Portal', icon: Rocket, tone: 'emerald' };

  const nurtureUntil = l.nurture_until && new Date(l.nurture_until) > new Date() ? l.nurture_until : null;
  const fu = l.next_follow_up_at || l.next_action_due;
  const overdue = fu && daysUntil(fu) < 0;

  if (l.nurture_until && new Date(l.nurture_until) <= new Date()) return { label: 'Nurture period ended — re-engage now', icon: Rocket, tone: 'rose' };
  if (repliedRecently(l)) return { label: 'Client replied — respond now', icon: Reply, tone: 'rose' };
  if (overdue) return { label: `Overdue — follow up now`, icon: AlertCircle, tone: 'danger', due: fu };
  if (!l.first_contacted_at && (s === 'new_lead' || !s)) return { label: 'Call new lead today', icon: PhoneCall, tone: 'blue' };
  if (l.portal_completed_at || s === 'ready_to_proceed') return { label: 'Ready to proceed to Client Portal', icon: Rocket, tone: 'emerald' };
  if (l.portal_sent_at && portalStarted(l)) return { label: 'Chase portal completion', icon: ExternalLink, tone: 'indigo' };
  if (l.portal_sent_at) {
    const h = minsSince(l.portal_sent_at);
    if (h !== null && h >= 1440) return { label: 'Portal sent — no activity, nudge', icon: ExternalLink, tone: 'orange' };
    return { label: 'Portal sent — awaiting start', icon: ExternalLink, tone: 'orange' };
  }
  if (l.discovery_call_at && daysUntil(l.discovery_call_at) >= 0) return { label: `Discovery call ${fmtRel(l.discovery_call_at)}`, icon: Target, tone: 'violet', due: l.discovery_call_at };
  if ((l.next_action || '').toLowerCase().includes('discovery')) return { label: 'Book / confirm discovery call', icon: Target, tone: 'violet' };
  if (nurtureUntil) return { label: `Long-term nurture — due ${fmtRel(nurtureUntil)}`, icon: Moon, tone: 'nurture', due: nurtureUntil };
  if (fu) {
    const method = l.follow_up_method || 'follow up';
    return { label: `${method} ${fmtRel(fu)}`, icon: Clock, tone: 'blue', due: fu };
  }
  if (l.next_action) return { label: l.next_action, icon: Target, tone: 'slate', due: l.next_action_due };
  return { label: 'Set next action', icon: Target, tone: 'slate' };
};

export const NBA_TONE = {
  danger:  { ring: 'ring-red-300', bg: 'bg-red-50', text: 'text-red-700', icon: 'text-red-600' },
  rose:    { ring: 'ring-rose-300', bg: 'bg-rose-50', text: 'text-rose-700', icon: 'text-rose-600' },
  orange:  { ring: 'ring-orange-300', bg: 'bg-orange-50', text: 'text-orange-800', icon: 'text-orange-600' },
  blue:    { ring: 'ring-blue-300', bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600' },
  violet:  { ring: 'ring-violet-300', bg: 'bg-violet-50', text: 'text-violet-700', icon: 'text-violet-600' },
  indigo:  { ring: 'ring-indigo-300', bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'text-indigo-600' },
  emerald: { ring: 'ring-emerald-300', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600' },
  nurture: { ring: 'ring-blue-300', bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600' },
  slate:   { ring: 'ring-slate-200', bg: 'bg-slate-50', text: 'text-slate-700', icon: 'text-slate-500' },
};

// ─── Activity signals ─────────────────────────────────────────
export const computeSignals = (l) => {
  const out = [];
  if (!l) return out;
  const upd = minsSince(l.updated_at);
  if (upd !== null && upd <= 15) out.push({ key: 'active', label: 'Active now', tone: 'emerald' });
  const rep = minsSince(l.last_client_response_at || l.last_response_at);
  if (rep !== null && rep <= 2880) out.push({ key: 'replied', label: `Replied ${fmtRel(l.last_client_response_at || l.last_response_at)}`, tone: 'blue' });
  const po = minsSince(l.portal_opened_at || l.portal_started_at);
  if (po !== null && po <= 1440) out.push({ key: 'portal', label: `Opened portal ${fmtRel(l.portal_opened_at || l.portal_started_at)}`, tone: 'purple' });
  if (l.portal_progress) out.push({ key: 'progress', label: `Portal ${l.portal_progress}% complete`, tone: 'indigo' });
  const attempts = l.contact_attempts || l.no_answer_attempts;
  if (attempts >= 2) out.push({ key: 'attempts', label: `${attempts} contact attempts`, tone: 'slate' });
  const lc = daysSince(l.last_contacted_at);
  if (lc !== null && lc >= 7) out.push({ key: 'stale', label: `No activity ${lc}d`, tone: 'amber' });
  return out.slice(0, 3);
};

export const SIGNAL_TONE = {
  emerald: 'text-emerald-700 bg-emerald-50 ring-emerald-200',
  blue: 'text-blue-700 bg-blue-50 ring-blue-200',
  purple: 'text-purple-700 bg-purple-50 ring-purple-200',
  indigo: 'text-indigo-700 bg-indigo-50 ring-indigo-200',
  amber: 'text-amber-700 bg-amber-50 ring-amber-200',
  slate: 'text-slate-600 bg-slate-50 ring-slate-200',
};

// ─── DerwentAI priority score (higher = more urgent) ──────────
export const priorityScore = (l) => {
  if (!l) return 0;
  if (l.nurture_phase === 'historical' || isPreReset(l)) return -1000;  // never surface in active/priority sorting
  let score = 0;
  const fu = l.next_follow_up_at || l.next_action_due;
  if (fu) { const du = daysUntil(fu); if (du !== null && du < 0) score += 120 + Math.min(60, -du * 4); else if (du === 0) score += 70; }
  if (repliedRecently(l)) score += 140;
  if (!l.first_contacted_at) score += 60;
  if (l.portal_sent_at && !l.portal_completed_at) score += 40;
  if (portalStarted(l) && !l.portal_completed_at) score += 30;
  if (l.discovery_call_at && daysUntil(l.discovery_call_at) === 0) score += 50;
  const t = effectiveTemp(l);
  score += { hot: 60, warm: 30, nurture: 8, cold: 0 }[t] || 0;
  const created = daysSince(l.created_at);
  if (created !== null) score += Math.max(0, 20 - created);
  if (l.status === 'long_term_nurture') score -= 40;
  if (l.nurture_until) { if (new Date(l.nurture_until) > new Date()) score -= 60; else score += 90; }
  return score;
};

// ─── DerwentAI behavioural temperature recommendation (rules-based) ───
export const nurtureReturned = (l) => !!(l?.nurture_until && new Date(l.nurture_until) <= new Date());

export const recommendTemperature = (l) => {
  if (!l) return { temp: 'cold', reason: '' };
  const repMin = minsSince(l.last_client_response_at || l.last_response_at);
  const attempts = l.no_answer_attempts || l.contact_attempts || 0;
  const created = daysSince(l.created_at);
  const future = l.nurture_until && new Date(l.nurture_until) > new Date();
  const longTf = ['6mo', '12mo', '12mo_plus'].includes(l.timeframe);
  if (repMin !== null && repMin <= 2880) return { temp: 'hot', reason: 'Client replied in the last 2 days' };
  if (l.portal_completed_at || l.status === 'ready_to_proceed') return { temp: 'hot', reason: 'Portal complete / confirmed proceeding' };
  if (l.portal_opened_at || l.portal_started_at) return { temp: 'hot', reason: 'Client started the portal' };
  if (l.discovery_call_at) return { temp: 'hot', reason: 'Discovery call booked / done' };
  if (l.portal_sent_at) return { temp: 'warm', reason: 'Portal invite sent — awaiting start' };
  if (attempts >= 3 || (!l.first_contacted_at && created !== null && created > 14)) return { temp: 'cold', reason: 'Multiple attempts with no response' };
  if (l.status === 'long_term_nurture' || future || longTf) return { temp: 'nurture', reason: 'Buying later / parked for now' };
  if (l.first_contacted_at) return { temp: 'warm', reason: 'Contacted, no appointment yet' };
  return { temp: 'cold', reason: 'No engagement yet' };
};

// ─── Pipeline volume goal (localStorage) ──────────────────────
export const PIPELINE_GOAL_KEY = 'ln_pipeline_goal_v1';
// Each returns a predicate over a lead. currentUserId used for "My Leads".
export const PRESETS = [
  { id: 'needs_attention', label: 'Needs Attention' },
  { id: 'new_today', label: 'New Today' },
  { id: 'no_contact', label: 'No Contact Yet' },
  { id: 'no_response', label: 'No Response' },
  { id: 'hot_leads', label: 'Hot Leads' },
  { id: 'discovery_booked', label: 'Discovery Booked' },
  { id: 'portal_sent', label: 'Portal Sent' },
  { id: 'portal_started', label: 'Portal Started' },
  { id: 'portal_stalled', label: 'Portal Stalled' },
  { id: 'nurture', label: 'Long-Term Nurture' },
  { id: 'ready_convert', label: 'Ready to Convert' },
  { id: 'historical', label: 'Historical (Pre-Reset)' },
];

const NURTURE_RESET_DATE = '2026-10-01';
export const isPreReset = (l) => {
  if (l?.reactivated_at) return false;
  if (l?.nurture_phase === 'historical') return true;
  const eff = l?.gsheet_submitted_at || l?.created_at || '';
  return !eff || eff.slice(0, 10) < NURTURE_RESET_DATE;
};
const isActive = (l) => !['lost', 'converted', 'moved_to_client_portal', 'graduated'].includes(l.status) && l.nurture_phase !== 'historical' && !isPreReset(l);

export const presetPredicate = (id, currentUserId) => {
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const map = {
    my_leads: (l) => l.assigned_broker_id === currentUserId,
    needs_attention: (l) => isActive(l) && (((l.next_follow_up_at || l.next_action_due) && daysUntil(l.next_follow_up_at || l.next_action_due) < 0) || !l.first_contacted_at),
    new_today: (l) => isActive(l) && l.created_at && new Date(l.created_at) >= todayStart,
    no_contact: (l) => isActive(l) && !l.first_contacted_at,
    no_response: (l) => isActive(l) && (l.no_answer_attempts >= 1 || l.contact_attempts >= 1) && !repliedRecently(l),
    hot_leads: (l) => isActive(l) && effectiveTemp(l) === 'hot',
    discovery_booked: (l) => isActive(l) && !!l.discovery_call_at && !l.portal_completed_at,
    portal_sent: (l) => isActive(l) && !!l.portal_sent_at && !l.portal_completed_at,
    portal_started: (l) => isActive(l) && !!l.portal_sent_at && portalStarted(l) && !l.portal_completed_at,
    portal_stalled: (l) => { if (!isActive(l) || !l.portal_sent_at || l.portal_completed_at) return false; const h = minsSince(l.portal_sent_at); return h !== null && h >= 1440 && !portalStarted(l); },
    nurture: (l) => l.status === 'long_term_nurture' || (l.nurture_until && new Date(l.nurture_until) > new Date()),
    ready_convert: (l) => isActive(l) && (l.status === 'ready_to_proceed' || !!l.portal_completed_at),
    historical: (l) => l.nurture_phase === 'historical' || isPreReset(l),
  };
  return map[id] || (() => true);
};

// Map a metric-card key → preset id (for click-to-filter).
export const METRIC_TO_PRESET = {
  new_today: 'new_today',
  requires_action: 'needs_attention',
  due_today: 'needs_attention',
  overdue: 'needs_attention',
  discovery_booked: 'discovery_booked',
  portal_sent: 'portal_sent',
  portal_not_started: 'portal_stalled',
  portal_started: 'portal_started',
  hot_opps: 'hot_leads',
  converted_week: 'ready_convert',
  moved_to_portal: null,
  lost_closed: null,
};

export const NURTURE_UNTIL_OPTIONS = [
  { id: '1w', label: '1 Week', days: 7 },
  { id: '2w', label: '2 Weeks', days: 14 },
  { id: '1m', label: '1 Month', days: 30 },
  { id: '3m', label: '3 Months', days: 90 },
  { id: '6m', label: '6 Months', days: 180 },
];

// Drop a card onto a kanban column → PATCH body (explicit stage + canonical side-effects).
export const stagePatchBody = (stageKey) => {
  const nowIso = new Date().toISOString();
  const body = { pipeline_stage: stageKey };
  switch (stageKey) {
    case 'new_lead': body.status = 'new_lead'; break;
    case 'contact_attempted': body.status = 'contacted'; break;
    case 'engaged': body.status = 'contacted'; break;
    case 'discovery_booked': body.status = 'discovery'; break;
    case 'discovery_completed': body.status = 'discovery'; body.discovery_call_at = nowIso; break;
    case 'portal_sent': body.portal_sent_at = nowIso; break;
    case 'portal_started': body.portal_sent_at = nowIso; body.portal_started_at = nowIso; break;
    case 'long_term_nurture': body.status = 'long_term_nurture'; break;
    case 'ready_to_proceed': body.status = 'ready_to_proceed'; break;
    case 'converted': body.status = 'converted'; break;
    case 'lost': body.status = 'lost'; break;
    default: break;
  }
  return body;
};
